import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import DragAndDropImage from "../../components/DragAndDropImage";
import { createMode } from "../../api/custom-mode";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { useAuth } from "../../contexts/AuthContext";
import { queueEvent } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { Button, Card, Field, Input, Spinner, Textarea } from "../../ui";

const ModeCreate = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isPremium, isLimitReached, showUpgradeModal } = useSubscription();

  const [name, setName] = useState("");
  const [info, setInfo] = useState("");
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    queueEvent(EVENTS.MODE_CREATE_SCREEN_VIEWED);
  }, []);

  const { mutate, isPending } = useMutation({
    mutationFn: () => createMode({ name: name.trim(), info: info.trim(), imageFile }),
    onSuccess: (mode) => {
      queryClient.invalidateQueries({ queryKey: ["myCustomModes"] });
      queueEvent(EVENTS.MODE_CREATED, { modeId: mode?._id });
      toast.success("تم إنشاء الفئة");
      navigate(mode?._id ? `/app/modes/${mode._id}` : "/app/profile");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "تعذّر إنشاء الفئة"),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const ownedCount = user?.customModesCount ?? 0;
    if (!isPremium && isLimitReached("MAX_CUSTOM_MODES", ownedCount)) {
      queueEvent(EVENTS.MODE_CREATE_LIMIT_REACHED, { count: ownedCount });
      showUpgradeModal({ messageKey: "CUSTOM_MODES_LIMIT" });
      return;
    }
    if (!name.trim()) {
      toast.error("اسم الفئة مطلوب");
      return;
    }
    if (!imageFile) {
      toast.error("صورة الفئة مطلوبة");
      return;
    }
    mutate();
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-start gap-2 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="-mr-2 mt-1 p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="رجوع"
        >
          <FaArrowRight />
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold">مصنع الفئات</h1>
          <p className="text-white/70 text-sm mt-1">إنشاء فئة جديدة</p>
        </div>
      </div>

      <Card as="form" onSubmit={handleSubmit} className="space-y-5">
        <Field label="اسم الفئة" htmlFor="mode-name">
          <Input
            id="mode-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="اسم الفئة"
          />
        </Field>

        <Field label="الوصف" htmlFor="mode-info" hint={`${info.length}/400`}>
          <Textarea
            id="mode-info"
            value={info}
            onChange={(e) => setInfo(e.target.value)}
            placeholder="وصف مختصر للفئة"
            rows={3}
            maxLength={400}
            className="resize-none"
          />
        </Field>

        <Field label="صورة الفئة">
          <DragAndDropImage onFileSelected={setImageFile} />
        </Field>

        <p className="text-white/55 text-xs leading-relaxed">
          بإنشائك للفئة فإنك توافق على{" "}
          <Link
            to="/mode-creation-terms"
            className="text-primary font-semibold underline underline-offset-2 hover:text-mainText"
            target="_blank"
          >
            شروط إنشاء الفئات
          </Link>
        </p>

        <Button
          type="submit"
          size="lg"
          block
          disabled={isPending}
          icon={isPending ? <Spinner /> : <FaCheck />}
        >
          إنشاء الفئة
        </Button>
      </Card>
    </div>
  );
};

export default ModeCreate;
